import type { NguoiXemNhieuKenh } from '@qlk/shared';

import { EmptyState } from '../../../../ui/index.js';
import { gonSo, phanTram } from '../dinhDang.js';
import { tenQuocGia } from '../hieuSuat/quocGia.js';
import { KhoiGop, ThanhNgang } from './KhoiGop.js';

/**
 * Tab "Người xem" của trang Hiệu suất.
 *
 * Bốn khối: quốc gia, độ tuổi/giới tính, thiết bị, người đăng ký.
 *
 * **Không có khối "giữ chân người xem"** — nó vẫn ở trang từng kênh. Giữ chân
 * là số của một video cụ thể trên một kênh cụ thể; gộp 16 kênh ra một đường
 * trung bình mà không ai đối soát được với Studio, và cũng không hành động được
 * gì từ nó.
 *
 * Mỗi khối mang **độ phủ riêng** (`KhoiGop`): quốc gia và độ tuổi chỉ có ở
 * những kỳ job `analytics-ham-nong` đã lưu, còn thiết bị và người đăng ký đọc
 * bảng theo ngày nên luôn đủ.
 */

/** Màu hai giới — token, không mã màu gõ tay. */
const MAU_NAM = 'var(--color-do-xem)';
const MAU_NU = 'var(--color-do-noi-dung)';

export interface TabNguoiXemProps {
  du: NguoiXemNhieuKenh | undefined;
  dangTai: boolean;
  loi: string | null;
}

export function TabNguoiXem({ du, dangTai, loi }: TabNguoiXemProps) {
  if (loi !== null) {
    return (
      <div className="rounded-the border border-vien bg-be-mat">
        <EmptyState loi title="Không tải được số liệu người xem" description={loi} />
      </div>
    );
  }

  const trongPhamVi = du?.soKenhTrongPhamVi ?? 0;

  return (
    <div className="flex flex-col gap-4">
      {/* Ghi chú đầu tab: cả bốn khối đều là **tỷ lệ gộp**, nói một lần. */}
      <p className="text-phu text-chu-mo">Tỷ lệ tính theo lượt xem, cộng gộp các kênh đang xem.</p>

      <div className="grid grid-cols-1 gap-4 @min-[1100px]/noi-dung:grid-cols-[3fr_2fr]">
        <KhoiGop
          tieuDe="Quốc gia hàng đầu"
          soKenhCoSoLieu={du?.quocGia.soKenhCoSoLieu ?? 0}
          soKenhTrongPhamVi={trongPhamVi}
          chuaCoBanLuu={du?.quocGia.chuaCoBanLuu ?? false}
          dangTai={dangTai}
          rong={(du?.quocGia.dong ?? []).length === 0}
          caoKhungXuong="16rem"
        >
          <ul className="flex flex-col gap-4">
            {(du?.quocGia.dong ?? []).map((q) => (
              <li key={q.country}>
                <div className="flex items-baseline justify-between gap-3 text-co-ban">
                  {/* Tên tiếng Việt dịch **ở giao diện** bằng `Intl.DisplayNames`
                      — máy chủ trả mã ISO, không nuôi bảng 250 dòng. */}
                  <span className="truncate text-chu">{tenQuocGia(q.country)}</span>
                  <span className="shrink-0 whitespace-nowrap">
                    <span className="so-lieu text-bang text-chu-mo">{gonSo(q.views)}</span>
                    <span className="so-lieu ml-2 font-semibold text-chu">
                      {phanTram(q.phanTram)}
                    </span>
                  </span>
                </div>
                <div className="mt-2">
                  <ThanhNgang phanTram={q.phanTram} />
                </div>
              </li>
            ))}
          </ul>
        </KhoiGop>

        <KhoiGop
          tieuDe="Độ tuổi và giới tính"
          soKenhCoSoLieu={du?.doTuoi.soKenhCoSoLieu ?? 0}
          soKenhTrongPhamVi={trongPhamVi}
          chuaCoBanLuu={du?.doTuoi.chuaCoBanLuu ?? false}
          dangTai={dangTai}
          rong={(du?.doTuoi.dong ?? []).length === 0}
          caoKhungXuong="16rem"
          phai={
            du === undefined ? null : (
              <span className="flex flex-wrap items-center gap-3 text-bang text-chu-phu">
                <ChuThich mau={MAU_NAM} nhan={`Nam ${phanTram(du.doTuoi.tongNam)}`} />
                <ChuThich mau={MAU_NU} nhan={`Nữ ${phanTram(du.doTuoi.tongNu)}`} />
              </span>
            )
          }
        >
          <DoTuoi dong={du?.doTuoi.dong ?? []} />
        </KhoiGop>
      </div>

      <div className="grid grid-cols-1 gap-4 @min-[1100px]/noi-dung:grid-cols-2">
        <KhoiGop
          tieuDe="Loại thiết bị"
          ghiChu="Phần trăm theo thời gian xem, như Studio"
          soKenhCoSoLieu={du?.thietBi.soKenhCoSoLieu ?? 0}
          soKenhTrongPhamVi={trongPhamVi}
          dangTai={dangTai}
          rong={(du?.thietBi.dong ?? []).length === 0}
          caoKhungXuong="10rem"
        >
          <ul className="flex flex-col gap-4">
            {(du?.thietBi.dong ?? []).map((t) => (
              <li key={t.deviceType}>
                <div className="flex items-baseline justify-between gap-3 text-co-ban">
                  <span className="truncate text-chu">{t.nhan}</span>
                  <span className="shrink-0 whitespace-nowrap">
                    <span className="so-lieu text-bang text-chu-mo">{gonSo(t.gioXem)} giờ</span>
                    <span className="so-lieu ml-2 font-semibold text-chu">
                      {phanTram(t.phanTram)}
                    </span>
                  </span>
                </div>
                <div className="mt-2">
                  <ThanhNgang phanTram={t.phanTram} />
                </div>
              </li>
            ))}
          </ul>
        </KhoiGop>

        <KhoiGop
          tieuDe="Thời gian xem của người đăng ký"
          soKenhCoSoLieu={du?.theoDangKy.soKenhCoSoLieu ?? 0}
          soKenhTrongPhamVi={trongPhamVi}
          dangTai={dangTai}
          rong={(du?.theoDangKy.dong ?? []).length === 0}
          caoKhungXuong="10rem"
        >
          <div>
            {/* Một thanh hai phần: tỷ trọng giữa hai nhóm đọc trong một cái nhìn. */}
            <span
              aria-hidden="true"
              className="flex h-2.5 overflow-hidden rounded-full bg-neutral-nen"
            >
              {(du?.theoDangKy.dong ?? []).map((t, i) => (
                <span
                  key={t.subscribedStatus}
                  className="block h-2.5"
                  style={{
                    width: `${String(t.phanTram)}%`,
                    background: i === 0 ? MAU_NAM : 'var(--color-do-trong)',
                  }}
                />
              ))}
            </span>

            <ul className="mt-4 flex flex-col gap-3">
              {(du?.theoDangKy.dong ?? []).map((t, i) => (
                <li
                  key={t.subscribedStatus}
                  className="flex items-baseline justify-between gap-3 text-co-ban"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="size-2.5 shrink-0 rounded-full"
                      style={{ background: i === 0 ? MAU_NAM : 'var(--color-do-trong)' }}
                    />
                    <span className="truncate text-chu">{t.nhan}</span>
                  </span>
                  <span className="shrink-0 whitespace-nowrap">
                    <span className="so-lieu text-bang text-chu-mo">{gonSo(t.gioXem)} giờ</span>
                    <span className="so-lieu ml-2 font-semibold text-chu">
                      {phanTram(t.phanTram)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </KhoiGop>
      </div>
    </div>
  );
}

/**
 * Sáu nhóm tuổi, mỗi nhóm **một thanh tách nam/nữ**.
 *
 * Hai đoạn trên cùng một thanh chứ không hai thanh rời: câu hỏi ở đây là "nhóm
 * tuổi này nghiêng về giới nào", và hai thanh cạnh nhau bắt mắt phải tự cộng.
 *
 * Thang chuẩn hoá theo **nhóm lớn nhất**, không theo 100 %: tổng của một nhóm
 * ít khi vượt 25 %, nên lấy 100 % làm trần thì cả sáu thanh đều ngắn tí và
 * không so được với nhau.
 */
function DoTuoi({ dong }: { dong: NguoiXemNhieuKenh['doTuoi']['dong'] }) {
  const lonNhat = Math.max(...dong.map((d) => d.nam + d.nu + d.tuXacDinh), 1);

  return (
    <ul className="flex flex-col gap-4">
      {dong.map((d) => {
        const tong = d.nam + d.nu + d.tuXacDinh;
        const ty = (x: number) => (x / lonNhat) * 100;

        return (
          <li key={d.ageGroup}>
            <div className="flex items-baseline justify-between gap-3 text-co-ban">
              <span className="so-lieu text-chu">{d.nhan}</span>
              <span className="so-lieu shrink-0 font-semibold text-chu">{phanTram(tong)}</span>
            </div>
            <span
              aria-hidden="true"
              className="mt-2 flex h-2.5 overflow-hidden rounded-full bg-neutral-nen"
            >
              <span
                className="block h-2.5"
                style={{ width: `${String(ty(d.nam))}%`, background: MAU_NAM }}
              />
              <span
                className="block h-2.5"
                style={{ width: `${String(ty(d.nu))}%`, background: MAU_NU }}
              />
              {d.tuXacDinh > 0 && (
                <span
                  className="block h-2.5"
                  style={{
                    width: `${String(ty(d.tuXacDinh))}%`,
                    background: 'var(--color-do-trong)',
                  }}
                />
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function ChuThich({ mau, nhan }: { mau: string; nhan: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span aria-hidden="true" className="size-2.5 rounded-full" style={{ background: mau }} />
      {nhan}
    </span>
  );
}
